import { useQuery } from '@tanstack/react-query';
import * as Dfns from 'date-fns/fp';
import {
  CreativeLambdaFetch,
  LinearOptimizationsLambdaFetch,
  SheetsLambdaFetch,
  awaitJSON
} from '../../utils/fetch-utils';

interface SheetRotationRow {
  status: string;
  channel: string;
  avail: string;
  name: string;
  day_set: string;
  daypart_beg: string;
  daypart_end: string;
}

interface NetworkRotationRow extends SheetRotationRow {
  fullName: string;
}

// network -> avail -> "name (day_set daypart_beg - daypart_end)" -> true
type BuyableNetworkAvailRotations = Record<string, Record<string, Record<string, true>>>;
// network -> rotation rows (with a display-ready fullName) for that network
type RotationsByNetwork = Record<string, NetworkRotationRow[]>;

const ROTATIONS_SHEET_PATH = '/json?file_id=1G33ftgb81YEjVR43WfWKx46sNBulocHspMdXtvTfFKo';
const isBuyableOrSecured = (row: SheetRotationRow) =>
  row.status === 'buyable' || row.status === 'secured';

const groupBy = <T>(rows: T[], keyOf: (row: T) => string): Record<string, T[]> => {
  const groups: Record<string, T[]> = {};
  for (const row of rows) {
    const key = keyOf(row);
    groups[key] = [...(groups[key] ?? []), row];
  }
  return groups;
};

const isValidDaypartTime = (time: string, suffix: string): boolean =>
  Dfns.isValid(Dfns.parseISO(`2016-01-01T${time.length === 4 ? '0' : ''}${time}${suffix}`));

// Google-Sheets-backed network/avail/rotation reference data used by the constraint editor's
// network/rotation dropdowns. Expensive (a full sheet fetch) and shared across every constraint
// section that needs it -- one React Query cache entry covers all of them instead of each section
// re-fetching independently.
export const useNetworkRotations = () =>
  useQuery({
    queryKey: ['linearNetworkRotations'],
    queryFn: async () => {
      const result = await SheetsLambdaFetch(ROTATIONS_SHEET_PATH);
      const { json } = await awaitJSON<{ json: SheetRotationRow[] }>(result);
      const buyableRows = json.filter(isBuyableOrSecured);

      const buyableNetworkAvailRotations: BuyableNetworkAvailRotations = {};
      for (const [network, networkRows] of Object.entries(
        groupBy(buyableRows, row => row.channel)
      )) {
        const availGroups = groupBy(networkRows, row => row.avail);
        buyableNetworkAvailRotations[network] = Object.fromEntries(
          Object.entries(availGroups).map(([avail, availRows]) => [
            avail,
            Object.fromEntries(
              availRows.map(row => [
                `${row.name} (${row.day_set} ${row.daypart_beg} - ${row.daypart_end})`,
                true as const
              ])
            )
          ])
        );
      }

      const validRotationRows = buyableRows.filter(
        row =>
          isValidDaypartTime(row.daypart_beg, ':00') &&
          isValidDaypartTime(row.daypart_end, ':00.000000')
      );
      const rotationsByNetwork: RotationsByNetwork = {};
      for (const [network, networkRows] of Object.entries(
        groupBy(validRotationRows, row => row.channel)
      )) {
        rotationsByNetwork[network] = networkRows.map(row => {
          const start = Dfns.parseISO(
            `2016-01-01T${row.daypart_beg.length === 4 ? '0' : ''}${row.daypart_beg}:00`
          );
          let end = Dfns.parseISO(
            `2016-01-01T${row.daypart_end.length === 4 ? '0' : ''}${row.daypart_end}:00.000000`
          );
          while (Dfns.getMinutes(end) % 30 !== 0) {
            end = Dfns.addMinutes(1, end);
          }
          return {
            ...row,
            fullName: `${row.name} (${row.day_set} ${Dfns.format('h:mma', start)}-${Dfns.format('h:mma', end)})`
          };
        });
      }

      return { buyableNetworkAvailRotations, rotationsByNetwork };
    }
  });

// insightsCategoriesData: available creative insight categories for this company.
// creativeInsightsData: creative name -> list of insight names, used by the constraint editor's
// "insights" multi-select and RunView's Export modal's "Creative Insight Category" grouping.
export const useCreativeInsights = (company: string) =>
  useQuery({
    queryKey: ['linearCreativeInsights', company],
    queryFn: async () => {
      const optionsResult = await CreativeLambdaFetch('/get_creative_options', {
        params: { cid: company }
      });
      const { insightsCategoryData } = await awaitJSON<{ insightsCategoryData: string[] }>(
        optionsResult
      );

      const insightsResult = await LinearOptimizationsLambdaFetch('/get_creative_insights', {
        params: { company }
      });
      const rows = await awaitJSON<{ creative: string; insight_name: string }[]>(insightsResult);
      const creativeInsightsData: Record<string, string[]> = Object.fromEntries(
        Object.entries(groupBy(rows, row => row.creative)).map(([creative, creativeRows]) => [
          creative,
          creativeRows.map(row => row.insight_name)
        ])
      );

      return { insightsCategoriesData: insightsCategoryData, creativeInsightsData };
    }
  });
